import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { environment } from 'environments/environment';

export interface UploadResultColumn {
    field: string;
    header: string;
}

export interface UploadResultsResponse {
    columns: UploadResultColumn[];
    pagination?: {
        totalPages: number;
        totalItems: number;
        pageNumber: number;
        pageSize: number;
        items: any[];
    };
    rows?: any[];
    totalCount?: number;
    page?: number;
    pageSize?: number;
}

export interface ExtractedResultsPage {
    page: number;
    // Each entry represents one extracted row for the page.
    lines: unknown[];
}

export interface ExtractedResultsResponse {
    header?: Record<string, unknown>;
    columnHeaders?: string[];
    pages?: ExtractedResultsPage[];
}

export interface DocumentImage {
    fileName: string;
    contentType: string;
    base64Data: string;
}

export interface DocumentImagesResponse {
    id: string;
    type: string;
    images: DocumentImage[];
}

@Injectable({ providedIn: 'root' })
export class UploadResultsService {
    // Endpoint for the main upload results table
    private readonly endpoint = `${environment.apiRoot}upload-results`;
    // Endpoint for the extracted results (inner table)
    private readonly extractedEndpoint = `${environment.apiRoot}extracted-results`;
    // Endpoint for document page images
    private readonly documentImagesEndpoint = `${environment.apiRoot}document-images`;

    constructor(private readonly http: HttpClient) {}

    getUploadResults(page: number, pageSize: number): Observable<UploadResultsResponse> {
        const params = new HttpParams()
            .set('sortBy', 'processDate')
            .set('sortDir', 'desc')
            .set('page', String(page))
            .set('pageSize', String(pageSize));

        return this.http.get<UploadResultsResponse>(this.endpoint, { params });
    }

    getExtractedResults(
        documentId: string,
        type: string,
        sessionId?: string
    ): Observable<ExtractedResultsResponse> {
        let params = new HttpParams()
            .set('type', type.trim().toLowerCase())
            .set('number', documentId);

        if (sessionId) {
            params = params.set('sessionid', sessionId);
        }

        return this.http.get<ExtractedResultsResponse>(this.extractedEndpoint, { params });
    }

    /**
     * Retrieve one or more document page images for the given document id and type.
     * The backend may return either a bare string array or an object with an `images` array;
     * this method normalises both into a simple `string[]` for the caller.
     */
    getDocumentImages(documentId: string, type: string): Observable<DocumentImage[]> {
        const params = new HttpParams()
            .set('id', documentId)
            .set('type', type.trim().toLowerCase());

        return this.http
            .get<DocumentImagesResponse>(this.documentImagesEndpoint, { params })
            .pipe(
                map((response) => {
                    return response?.images ?? [];
                })
            );
    }
}
